import { jsPDF } from "jspdf";
import "jspdf-autotable";
import {
  ellipsisString,
  formatDateNormal,
  formatByCountry
} from "@/app/infrastructures/misc/Utils";
import { HubManifestDetail } from "@/domain/entities/HubToHub";
import { AccountController } from "@/app/ui/controllers/AccountController";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";
import { createI18n } from "vue-i18n";

let doc: any;
let translate: any = {};

const drawCellDataConditions = (cell: any) => [
  cell.column.dataKey === "totalPieces" &&
    (cell.row.section === "head" || cell.row.section === "body"),
  cell.column.dataKey === "productType" && cell.row.section === "head",
  cell.column.dataKey === "productType" && cell.row.section === "body",
  cell.column.dataKey === "origin" && cell.row.section === "body"
];

const drawCellData = (cell: any) => {
  if (cell.row.index < 0) {
    return;
  }

  if (drawCellDataConditions(cell)[2]) {
    doc.setFillColor("#ebecf0");
    doc.setFont("Montserrat", "bold");
    doc.setTextColor("#4D4D4D");
    const text = cell.row.cells["productType"].text || [];
    text
      .filter((item: any) => item !== "")
      .map((_: any, index: number) => {
        doc.roundedRect(
          cell.row.cells["productType"].x + 0.7,
          cell.row.cells["productType"].y +
            cell.row.cells["productType"].contentHeight /
              (1.6333 +
                1.75 *
                  (cell.row.cells["totalPieces"].text || []).filter(
                    (item: any) => item !== ""
                  ).length) +
            (0.0083 + 5.675 * index),
          cell.row.cells["productType"].contentWidth - 1,
          3,
          1,
          1,
          "F"
        );
      });
    doc.setFillColor("#fff");
  }
  if (drawCellDataConditions(cell)[3]) {
    doc.setFillColor("#ebecf0");
    const text = cell.row.cells["origin"].text || [];
    text
      .filter((item: any) => item !== "")
      .map((_: any, index: number) => {
        doc.roundedRect(
          cell.row.cells["origin"].x + 0.7,
          cell.row.cells["origin"].y + 2.55,
          cell.row.cells["origin"].contentWidth - 1,
          3,
          1,
          1,
          "F"
        );
      });
    doc.setFillColor("#fff");
  }
};

const generateInHubManifestPdf = async (
  hmNo: string,
  data: any,
  logo: string,
  head: HubManifestDetail,
  outhub: boolean
) => {
  doc = new jsPDF("p", "mm", "a4", true);
  const pdfWidth = doc.internal.pageSize.width;

  const i18nInstance = createI18n({
        legacy: false,
        locale:
          AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
        fallbackLocale: "id",
        globalInjection: false,
        messages: {
          en,
          id,
          my
        }
      });
  translate = i18nInstance.global.t;

  try {
    const img = new Image();
    img.src = logo;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const imageWidth = 40;
    const imageHeight = (img.height / img.width) * imageWidth;

    doc.addImage(
      img,
      "PNG",
      pdfWidth - imageWidth - 9,
      11,
      imageWidth,
      imageHeight,
      undefined,
      "FAST"
    );
  } catch (error) {
    console.error("Error loading logo:", error);
  }

  doc.setFont("Montserrat", "bold");
  doc.setFontSize(14);
  doc.setTextColor("#4D4D4D");
  doc.text(`${outhub ? "Outhub" : "Inhub"} Manifest - ${hmNo}`, 10, 18);
  doc.setFillColor("#e0e0e0");
  doc.rect(10, 25, 190, 0.2, "F");
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#4d4d4d");
  // left column
  doc.text(outhub ? translate("hubToHub.manifest.hubOriginDestination") : translate("hubToHub.manifest.hubLocation"), 10, 31);
  doc.text(":", 43, 31);
  doc.text(
    ellipsisString(
      outhub
        ? `${head.hmHubName} - ${head.hmHubDestinationName}`
        : head.hmHubName || "",
      70
    ),
    45,
    31
  );

  doc.setTextColor("#4d4d4d");
  doc.text(translate("hubToHub.manifest.consoleLocation"), 10, 37);
  doc.text(":", 43, 37);
  doc.text(
    `${AccountController.accountData?.account_location?.city_code ||
      ""} - ${AccountController.accountData?.account_location?.city_name ||
      ""}`,
    45,
    37
  );

  // center column
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#4d4d4d");
  doc.text(translate("hubToHub.manifest.totalSttPieces"), 90, 31);
  doc.text(":", 115, 31);
  doc.text(
    `${formatByCountry(head.hmStts || 0, {
      type: "quantity"
    })}/${formatByCountry(head.hmSttPieces || 0, { type: "quantity" })}`,
    117,
    31
  );

  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#4d4d4d");
  doc.text(translate("hubToHub.manifest.weight"), 90, 37);
  doc.text(":", 115, 37);
  doc.text(
    formatByCountry(head.hmSttGrossWeights || 0, {
      type: "weight",
      suffix: " kg"
    }),
    117,
    37
  );

  // right column
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#4d4d4d");
  doc.text(translate("hubToHub.manifest.manifestDate"), 140, 31);
  doc.text(":", 168, 31);
  doc.text(formatDateNormal(head.hmCreatedAt, "DD MMMM YYYY, HH:mm"), 170, 31);

  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#4d4d4d");
  doc.text(translate("hubToHub.manifest.createdBy"), 140, 37);
  doc.text(":", 168, 37);
  doc.text(head.hmCreatedByName || "-", 170, 37);

  let lastPage = 0;
  let lastY = 0;
  let lastHeight = 0;
  const dataLength = data?.length || 0;
  const columnsTable = [
    {
      header: "No.",
      dataKey: "no"
    },
    {
      header: "No. bag/dem",
      dataKey: "bagNumber"
    },
    {
      header: "No. STT",
      dataKey: "sttNumber"
    },
    {
      header: translate("hubToHub.manifest.noReff"),
      dataKey: "referenceNumber"
    },
    {
      header: translate("hubToHub.manifest.totalPieces"),
      dataKey: "totalPieces"
    },
    {
      header: translate("hubToHub.manifest.weight"),
      dataKey: "grossWeight"
    },
    {
      header: translate("hubToHub.manifest.product"),
      dataKey: "productType"
    }
  ];

  const columnStyles = {
    0: { cellWidth: 10 },
    1: { cellWidth: 35 },
    2: { cellWidth: 35 },
    3: { cellWidth: 35 },
    4: { cellWidth: 20 },
    5: { cellWidth: 25 },
    6: { cellWidth: 30 }
  };

  doc.autoTable({
    body: data,
    columns: columnsTable,
    theme: "plain",
    startY: doc.internal.getCurrentPageInfo().pageNumber === 1 ? 43 : 0,
    margin: { top: 7, left: 10, right: 10, bottom: 7 },
    headStyles: {
      font: "Montserrat",
      fontStyle: "bold",
      fillColor: "#F5F6F7",
      textColor: "#4D4D4D",
      fontSize: 7,
      halign: "left",
      valign: "top",
      cellPadding: { top: 4, right: 2, bottom: 4, left: 2 }
    },
    bodyStyles: {
      font: "Montserrat",
      fontStyle: "normal",
      fillColor: "#fff",
      textColor: "#4D4D4D",
      fontSize: 7,
      halign: "left",
      valign: "top",
      lineColor: "#e0e0e0",
      lineWidth: { top: 0.1, right: 0, bottom: 0.1, left: 0 },
      cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
      cellWidth: "auto"
    },
    columnStyles: columnStyles,
    willDrawCell: (cell: any) => {
      drawCellData(cell);
      lastPage = cell.pageNumber;
      lastY = cell.cursor.y;
      lastHeight = cell.row.height;
      if (cell.row.index === dataLength - 2 && lastY + lastHeight + 20 > 270) {
        cell.cell.contentHeight = 100;
        cell.row.height = 100;
      }
    }
  });
  lastY = lastY + lastHeight + 20;
  lastY = lastY > 270 ? 270 : lastY;
  doc.setPage(lastPage);
  doc.setFontSize(8);
  doc.text("Staff Admin", 12 + 90, lastY);
  doc.text("(.................................)", 10 + 90, lastY + 20);
  doc.text("Kurir", 50 + 90, lastY);
  doc.text("(.................................)", 45 + 90, lastY + 20);
  doc.text("Operations Supervisor", 80 + 90, lastY);
  doc.text("(.................................)", 85 + 90, lastY + 20);

  doc.setProperties({
    title: `${outhub ? "Outhub" : "Inhub"} Manifest - ${hmNo}`
  });
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

const manifestPdf = async (
  hmNo: string,
  printData: HubManifestDetail,
  logo: any,
  outhub: boolean
) => {
  const data: Array<any> = [];
  let no = 1;
  if (printData?.stts) {
    for (const e of printData.stts) {
      data.push({
        no: no,
        bagNumber: e?.hmdBagNo || e?.hmdDemNo || "-",
        sttNumber: e?.hmdSttNo || "-",
        referenceNumber: e?.hmdRefNo || "-",
        totalPieces:
          e?.hmdPieces !== undefined
            ? formatByCountry(e.hmdPieces, { type: "quantity" })
            : "-",
        grossWeight: e?.hmdGrossWeight
          ? formatByCountry(e.hmdGrossWeight, { type: "weight", suffix: " kg" })
          : "-",
        productType: e?.hmdProductType || "-"
      });
      no++;
    }
  }
  await generateInHubManifestPdf(hmNo, data, logo, printData, outhub);
};

export default manifestPdf;
